/**
 * Notification History Component
 *
 * Displays user's notification history from Firestore
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, CheckCircle, AlertCircle, Info, Archive } from 'lucide-react';
import { archiveUserNotifications } from '@/services/queueService';
import { toast } from 'sonner';

interface NotificationDocument {
  notificationId: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, any>;
  read: boolean;
  createdAt: Timestamp;
  archivedByUser?: boolean;
}

export function NotificationHistory() {
  const { t, i18n } = useTranslation();
  const { firebaseUser, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [notifications, setNotifications] = useState<NotificationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);

  // Use useCallback to memoize fetchNotifications so it can be safely used as a dependency
  const fetchNotifications = useCallback(async () => {
    if (!firebaseUser) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const notificationsRef = collection(db, 'notifications');

    try {
      const q = query(
        notificationsRef,
        where('userId', '==', firebaseUser.uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      let notifs = snapshot.docs.map(doc => ({
        notificationId: doc.id,
        ...doc.data()
      })) as NotificationDocument[];

      // Filter archived notifications client-side for regular users
      if (!isSuperAdmin) {
        notifs = notifs.filter(n => n.archivedByUser !== true);
      }

      setNotifications(notifs);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // If orderBy fails, try without it
      try {
        const simpleQ = query(
          notificationsRef,
          where('userId', '==', firebaseUser.uid),
          limit(50)
        );

        const snapshot = await getDocs(simpleQ);
        let notifs = snapshot.docs.map(doc => ({
          notificationId: doc.id,
          ...doc.data()
        })) as NotificationDocument[];

        // Sort client-side
        notifs.sort((a, b) => {
          const aTime = a.createdAt?.toMillis() || 0;
          const bTime = b.createdAt?.toMillis() || 0;
          return bTime - aTime;
        });

        // Filter archived notifications client-side for regular users
        if (!isSuperAdmin) {
          notifs = notifs.filter(n => n.archivedByUser !== true);
        }

        setNotifications(notifs);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
      }
      setLoading(false);
    }
  }, [firebaseUser, isSuperAdmin]);

  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Set up a simple interval to refresh every 10 seconds
    const interval = setInterval(fetchNotifications, 10000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleArchiveNotifications = async () => {
    // Debug: Log authentication status
    console.log('🔍 Archiving notifications - Auth status:', {
      firebaseUser: firebaseUser ? {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified
      } : null,
      user: user ? {
        userId: user.id,
        role: user.role
      } : null
    });

    if (!firebaseUser) {
      console.error('❌ No firebaseUser - user must be authenticated');
      toast.error(t('notifications.mustBeAuthenticated'));
      return;
    }

    setArchiving(true);
    try {
      // 🔧 FIX: Ensure user has a valid auth token before calling the Cloud Function
      // This fixes the "request was not authenticated" error
      console.log('🔐 Refreshing auth token before calling Cloud Function...');
      const token = await firebaseUser.getIdToken(true);
      console.log('✅ Auth token refreshed:', token ? 'Token exists' : 'No token');

      console.log('📤 Calling archiveUserNotifications...');
      const result = await archiveUserNotifications();
      console.log('✅ archiveUserNotifications result:', result);

      if (result.success) {
        toast.success(result.message);
        // Immediately refresh the notifications list
        await fetchNotifications();
      }
    } catch (err) {
      console.error('❌ Failed to archive notifications:', err);
      toast.error(t('notifications.archiveError'));
    } finally {
      setArchiving(false);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'ticket_confirmed':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'your_turn':
        return <Bell className="h-5 w-5 text-info" />;
      case 'queue_warning':
        return <AlertCircle className="h-5 w-5 text-accent" />;
      default:
        return <Info className="h-5 w-5 text-ink-muted" />;
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.now');
    if (diffMins < 60) return t('notifications.minutesAgo', { minutes: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { hours: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { days: diffDays });

    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info/30"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="text-center py-12">
        <Bell className="h-12 w-12 text-ink-subtle mx-auto mb-4" />
        <h3 className="text-lg font-medium text-ink mb-2">
          {t('notifications.none')}
        </h3>
        <p className="text-ink-subtle">
          {t('notifications.emptyDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink">
          {t('notifications.history')}
        </h3>
        {notifications.length > 0 && (
          <button
            onClick={handleArchiveNotifications}
            disabled={archiving}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-2 text-ink-muted rounded-lg hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <Archive className="w-4 h-4" />
            {archiving ? t('notifications.clearing') : t('notifications.clearHistory')}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.notificationId}
            className={`p-4 rounded-lg border transition ${
              notification.read
                ? 'bg-surface border-hairline'
                : 'bg-info/10 border-info/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(notification.type)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-ink">
                    {notification.title}
                  </p>
                  {notification.archivedByUser && isSuperAdmin && (
                    <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent rounded">
                      {t('notifications.archived')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-ink-muted mt-1">
                  {notification.body}
                </p>
                <p className="text-xs text-ink-subtle mt-1">
                  {formatDate(notification.createdAt)}
                </p>
              </div>

              {!notification.read && (
                <div className="flex-shrink-0">
                  <span className="inline-block w-2 h-2 bg-info rounded-full"></span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
